import {Client, ClientUser} from '../../../API';
import { ConflictHandler, RefetchOptions, MergeOptions } from '../../conflictResolution/types';
import {applyMerge} from "../applyMerge";

/**
 * Fields that should never be modified by merge (immutable or system-managed)
 */
const IMMUTABLE_FIELDS = ['id', 'ownerId', 'sub', 'email', 'createdAt', '__typename'];

/**
 * Fields that should always come from current state (system-managed)
 */
const SYSTEM_FIELDS = ['version', 'updatedAt', 'deletedAt', '_lastChangedAt'];

/**
 * Conflict handler for ClientUser entities.
 * Handles version conflicts during client user updates with intelligent merging.
 * 
 * Merge Strategy:
 * 1. Start with complete currentState (all server fields)
 * 2. Apply user's intendedChanges except for immutable/system fields
 * 3. Always use currentState.version for retry
 * 
 */
export const clientUserConflictHandler: ConflictHandler<ClientUser> = {
    entityType: 'ClientUser',

    async refetch({ entityId, dataClientService, authService }: RefetchOptions): Promise<ClientUser> {
        // Fallback refetch - server should provide currentState
        throw new Error(
            'ClientUser refetch not implemented - server should provide currentState in conflict response'
        );
    },

    merge({ currentState, intendedChanges }: MergeOptions<ClientUser>): Partial<ClientUser> {
        const merged: Partial<ClientUser> = {};

        // Start with current state
        Object.assign(merged, currentState);

        // Apply intended changes, respecting field categories
        applyMerge<ClientUser>(merged, intendedChanges, IMMUTABLE_FIELDS, SYSTEM_FIELDS);

        // Ensure version is from current state (critical for retry)
        merged.version = currentState.version;

        return merged;
    },

    maxRetries: 3,

    shouldRetry(conflict, attemptNumber): boolean {
        // Don't retry if entity was deleted
        if (conflict.conflictType === 'ENTITY_DELETED') {
            return false;
        }
        // Don't retry beyond max attempts
        return attemptNumber < 3;
    }
};
